<script setup lang="ts">
import FilterPopoverBase from '../modules/FilterPopoverBase.vue'
import type {
  EstateListingEssentialResponse,
  EstateListingResponse,
} from '~/types/api/EstateListing'
import type { useFilterState } from '~/composables/filter/useFilterState'
import { useRangeSliderState } from '~/composables/filter/useRangeSliderState'
import { useFilterPopoverState } from '~/composables/filter/useFilterPopoverState'

const props = defineProps<{
  officesResponse: EstateListingResponse | EstateListingEssentialResponse
  filterState: ReturnType<typeof useFilterState>
}>()

const sliderRef = ref<HTMLDivElement | null>(null)
const filterPopoverState = useFilterPopoverState()
const rangeSliderState = useRangeSliderState({
  filterState: props.filterState,
  serverFilterValues: props.officesResponse.filter,
  filterKey: 'rent',
})

onMounted(() => {
  rangeSliderState.initializeSlider(sliderRef.value)
})

onBeforeUnmount(() => {
  rangeSliderState.destroySlider()
})
</script>

<template>
  <FilterPopoverBase
    :filter-popover-state="filterPopoverState"
    :show-reset-button="rangeSliderState.isFiltered.value"
  >
    <template #trigger-label>{{ $t('filter.price.title') }}</template>

    <template #trigger-value>
      {{ rangeSliderState.dropdownPlaceholderValue.value }} € / m<sup>2</sup>
    </template>

    <template #popover-body>
      <div class="c-filter-price">
        <div class="c-filter-price__title">{{ $t('filter.price.title') }}</div>

        <div class="c-filter-price__inputs">
          <IInput
            v-model="rangeSliderState.inputValues.from.value"
            :placeholder="rangeSliderState.inputPlaceholder.value.from"
            size="sm"
            @update:model-value="rangeSliderState.onFromInputUpdate"
            @blur="rangeSliderState.onFromInputBlur"
            @change="rangeSliderState.onFromInputChange"
          >
            <template #suffix>€ / m<sup>2</sup></template>
          </IInput>

          <IInput
            v-model="rangeSliderState.inputValues.to.value"
            :placeholder="rangeSliderState.inputPlaceholder.value.to"
            size="sm"
            @update:model-value="rangeSliderState.onToInputUpdate"
            @blur="rangeSliderState.onToInputBlur"
            @change="rangeSliderState.onToInputChange"
          >
            <template #suffix>€ / m<sup>2</sup></template>
          </IInput>
        </div>

        <div class="c-filter-price__slider">
          <div ref="sliderRef" />
        </div>
      </div>
    </template>
  </FilterPopoverBase>
</template>

<style lang="scss">
@import 'nouislider/dist/nouislider.css';
@import '~/assets/styles/lib/nouislider-overrides/overrides';
</style>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-filter-price {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__inputs {
    display: flex;
    gap: 8px;

    :deep(.input) {
      .input-suffix {
        font-size: 14px;
        font-weight: 600;
        padding-left: 0;
        padding-right: 8px;
        border: none;
      }

      & > input {
        font-size: 16px;
        padding-right: 2px;
        width: 86px;
      }
    }
  }
}
</style>
